import { cn } from '@/lib/utils';
import { formatPercentage, formatCurrency } from '@/lib/foco-calculations';
import { Info, Home, ShoppingBag, PiggyBank } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown } from 'lucide-react';

interface BudgetRuleGuideProps {
  totalIncome: number;
  fixedExpenses: number;
  variableExpenses: number;
  savings: number;
  investments: number;
  debtPayments: number;
  currency: string;
}

interface CategoryData {
  label: string;
  icon: typeof Home;
  guidePct: number;
  actualPct: number;
  actualAmount: number;
  guideAmount: number;
  colorClass: string;
  bgClass: string;
  progressColor: string;
}

export function BudgetRuleGuide({
  totalIncome,
  fixedExpenses,
  variableExpenses,
  savings,
  investments,
  debtPayments,
  currency,
}: BudgetRuleGuideProps) {
  if (totalIncome <= 0) return null;

  const needsAmount = fixedExpenses;
  const wantsAmount = variableExpenses;
  const goalsAmount = savings + investments + debtPayments;

  const needsPct = needsAmount / totalIncome;
  const wantsPct = wantsAmount / totalIncome;
  const goalsPct = goalsAmount / totalIncome;

  const categories: CategoryData[] = [
    {
      label: 'Necesidades',
      icon: Home,
      guidePct: 0.5,
      actualPct: needsPct,
      actualAmount: needsAmount,
      guideAmount: totalIncome * 0.5,
      colorClass: 'text-blue-600 dark:text-blue-400',
      bgClass: 'bg-blue-500/10',
      progressColor: 'bg-blue-500',
    },
    {
      label: 'Estilo de vida',
      icon: ShoppingBag,
      guidePct: 0.3,
      actualPct: wantsPct,
      actualAmount: wantsAmount,
      guideAmount: totalIncome * 0.3,
      colorClass: 'text-amber-600 dark:text-amber-400',
      bgClass: 'bg-amber-500/10',
      progressColor: 'bg-amber-500',
    },
    {
      label: 'Metas financieras',
      icon: PiggyBank,
      guidePct: 0.2,
      actualPct: goalsPct,
      actualAmount: goalsAmount,
      guideAmount: totalIncome * 0.2,
      colorClass: 'text-emerald-600 dark:text-emerald-400',
      bgClass: 'bg-emerald-500/10',
      progressColor: 'bg-emerald-500',
    },
  ];

  const getStatus = (cat: CategoryData) => {
    const diff = cat.actualPct - cat.guidePct;
    // For needs/wants: being under is good, over is bad
    // For goals: being over is good, under is bad
    if (cat.guidePct === 0.2) {
      // Goals category - more is better
      if (cat.actualPct >= cat.guidePct) return { label: '✓ En meta', class: 'text-success' };
      if (cat.actualPct >= cat.guidePct * 0.5) return { label: 'Cerca', class: 'text-warning' };
      return { label: 'Por mejorar', class: 'text-destructive' };
    } else {
      // Needs/Wants - less is better
      if (cat.actualPct <= cat.guidePct) return { label: '✓ En meta', class: 'text-success' };
      if (cat.actualPct <= cat.guidePct * 1.2) return { label: 'Cerca', class: 'text-warning' };
      return { label: 'Excedido', class: 'text-destructive' };
    }
  };

  return (
    <div className="rounded-xl border bg-card p-4 sm:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base sm:text-lg font-semibold">Guía 50 / 30 / 20</h3>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Distribución intencional de tu flujo de caja
          </p>
        </div>
      </div>

      {/* Visual bars */}
      <div className="space-y-4">
        {/* Guide reference bar */}
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">Guía recomendada</p>
          <div className="flex h-3 rounded-full overflow-hidden">
            <div className="bg-blue-500/70 w-[50%]" />
            <div className="bg-amber-500/70 w-[30%]" />
            <div className="bg-emerald-500/70 w-[20%]" />
          </div>
          <div className="flex text-[10px] text-muted-foreground">
            <span className="w-[50%]">50%</span>
            <span className="w-[30%]">30%</span>
            <span className="w-[20%]">20%</span>
          </div>
        </div>

        {/* Actual distribution bar */}
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">Tu distribución actual</p>
          <div className="flex h-3 rounded-full overflow-hidden bg-muted">
            <div 
              className="bg-blue-500 transition-all duration-500" 
              style={{ width: `${Math.min(needsPct * 100, 100)}%` }} 
            />
            <div 
              className="bg-amber-500 transition-all duration-500" 
              style={{ width: `${Math.min(wantsPct * 100, 100 - Math.min(needsPct * 100, 100))}%` }} 
            />
            <div 
              className="bg-emerald-500 transition-all duration-500" 
              style={{ width: `${Math.min(goalsPct * 100, Math.max(0, 100 - Math.min(needsPct * 100, 100) - Math.min(wantsPct * 100, 100)))}%` }} 
            />
          </div>
          <div className="flex text-[10px] text-muted-foreground">
            <span style={{ width: `${Math.max(needsPct * 100, 10)}%` }}>{formatPercentage(needsPct)}</span>
            <span style={{ width: `${Math.max(wantsPct * 100, 10)}%` }}>{formatPercentage(wantsPct)}</span>
            <span>{formatPercentage(goalsPct)}</span>
          </div>
        </div>
      </div>

      {/* Category details */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {categories.map((cat) => {
          const status = getStatus(cat);
          const Icon = cat.icon;
          const pctOfGuide = cat.guidePct === 0.2
            ? Math.min((cat.actualPct / cat.guidePct) * 100, 100)
            : Math.min(((cat.guidePct - Math.max(0, cat.actualPct - cat.guidePct)) / cat.guidePct) * 100, 100);

          return (
            <div key={cat.label} className={cn('rounded-lg p-3 space-y-2', cat.bgClass)}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon className={cn('h-4 w-4', cat.colorClass)} />
                  <span className="text-sm font-semibold">{cat.label}</span>
                </div>
                <span className={cn('text-xs font-bold', status.class)}>{status.label}</span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-lg font-bold">{formatPercentage(cat.actualPct)}</span>
                <span className="text-xs text-muted-foreground">
                  meta: {formatPercentage(cat.guidePct)}
                </span>
              </div>
              <div className="flex items-baseline justify-between text-xs text-muted-foreground">
                <span>{formatCurrency(cat.actualAmount, currency)}</span>
                <span>de {formatCurrency(cat.guideAmount, currency)}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Personalized tips */}
      {(() => {
        const tips: { emoji: string; text: string }[] = [];

        if (needsPct > 0.5) {
          tips.push({
            emoji: '🏠',
            text: `Tus gastos fijos representan ${formatPercentage(needsPct)} de tus ingresos (meta: 50%). Revisa suscripciones que no uses, renegocia seguros o busca alternativas más económicas en servicios.`,
          });
        }
        if (wantsPct > 0.3) {
          tips.push({
            emoji: '🛍️',
            text: `Tus gastos variables están en ${formatPercentage(wantsPct)} (meta: 30%). Intenta planificar comidas semanales, establece un presupuesto para entretenimiento y usa la regla de 24h antes de compras impulsivas.`,
          });
        }
        if (goalsPct < 0.2 && totalIncome > 0) {
          tips.push({
            emoji: '🎯',
            text: `Solo destinas ${formatPercentage(goalsPct)} a metas financieras (meta: 20%). Automatiza transferencias a ahorro el día de pago, aunque sea un monto pequeño. La consistencia importa más que la cantidad.`,
          });
        }
        if (needsPct <= 0.5 && wantsPct <= 0.3 && goalsPct >= 0.2) {
          tips.push({
            emoji: '🏆',
            text: '¡Excelente! Tu distribución está alineada con la guía 50/30/20. Mantén este equilibrio y considera aumentar gradualmente el porcentaje destinado a metas financieras.',
          });
        }
        if (needsPct > 0.6) {
          tips.push({
            emoji: '⚠️',
            text: 'Tus gastos fijos superan el 60% de tus ingresos. Esto deja poco margen. Evalúa si puedes reducir costos de vivienda, transporte o consolidar deudas para liberar flujo.',
          });
        }

        if (tips.length === 0) return null;

        return (
          <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
            <p className="text-xs font-semibold text-foreground">💡 Consejos personalizados</p>
            {tips.map((tip, i) => (
              <p key={i} className="text-xs text-muted-foreground leading-relaxed">
                {tip.emoji} {tip.text}
              </p>
            ))}
          </div>
        );
      })()}

      {/* Explanation */}
      <Collapsible>
        <CollapsibleTrigger asChild>
          <button className="flex items-center gap-2 w-full px-3 py-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors group text-left">
            <Info className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground">¿Qué es la regla 50/30/20?</span>
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground ml-auto transition-transform group-data-[state=open]:rotate-180" />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2">
          <div className="rounded-lg bg-muted/30 border p-3 space-y-2 text-xs text-muted-foreground">
            <p>
              La regla <strong className="text-foreground">50/30/20</strong> es una guía popular de presupuesto creada por Elizabeth Warren. 
              Sugiere dividir tus ingresos netos en tres categorías:
            </p>
            <ul className="space-y-1.5 ml-1">
              <li className="flex items-start gap-2">
                <span className="h-2 w-2 rounded-full bg-blue-500 mt-1 flex-shrink-0" />
                <span><strong className="text-foreground">50% Necesidades:</strong> Gastos fijos esenciales como vivienda, servicios, seguros y suscripciones básicas.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="h-2 w-2 rounded-full bg-amber-500 mt-1 flex-shrink-0" />
                <span><strong className="text-foreground">30% Estilo de vida:</strong> Gastos variables como alimentación, transporte, entretenimiento y compras personales.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="h-2 w-2 rounded-full bg-emerald-500 mt-1 flex-shrink-0" />
                <span><strong className="text-foreground">20% Metas financieras:</strong> Ahorro, inversiones y pago de deudas para construir tu futuro.</span>
              </li>
            </ul>
            <p className="pt-1 border-t italic">
              💡 Esta es una guía general, no una regla rígida. Adapta los porcentajes según tu realidad, 
              etapa de vida y objetivos personales. Lo importante es tener una distribución 
              <strong className="text-foreground"> intencional</strong> de tu dinero.
            </p>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}